import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, ChevronRight, Copy as CopyIcon, Eye, EyeOff, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { CONNECT_EXCHANGES } from "@/data/exchanges";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

const PICKER_FAQ = [
  { q: "What is an API key?", a: "An API key is a secure credential that lets Kalki read your balances and place trades on your exchange account — without ever accessing withdrawals." },
  { q: "Why should I connect my API key?", a: "Connecting a key lets us execute strategies and track performance on the client's account automatically and in real time." },
  { q: "Is it safe to connect my API key?", a: "Yes. Keys are stored encrypted server-side, never exposed in the browser, and we recommend restricting them to our Trusted IPs with withdrawals disabled." },
];

const CONNECT_FAQ = [
  { q: "How to create a new API key on Binance?", a: "In your exchange account, open API Management, create a new key labelled 'Kalki', and enable Read + Trade permissions only." },
  { q: "How to white list your IP on my exchange account?", a: "In the API key settings, choose 'Restrict access to trusted IPs only' and paste the IP addresses shown here." },
  { q: "How to connect my API key to your service?", a: "Copy the API key and Secret from your exchange and paste them into the fields on this page, then click Next." },
  { q: "Why is it important to whitelist your IP address?", a: "IP whitelisting ensures the key can only be used from Kalki's servers, dramatically reducing the risk of misuse." },
];

const EXCHANGE_TYPES = [
  { value: "both", label: "Both" },
  { value: "spot", label: "Only Spot" },
  { value: "futures", label: "Only Futures" },
];

function ExchangeTile({ ex, selected, onSelect }) {
  return (
    <button
      onClick={() => onSelect(ex)}
      data-testid={`connect-exchange-${ex.slug}`}
      className={cn("relative text-center rounded-lg border p-5 flex flex-col items-center gap-3 transition-all",
        selected ? "border-[#D4AF37] bg-[rgba(212,175,55,0.06)]" : "border-[#262626] bg-[#0D0D0D] hover:border-[#525252]")}
    >
      {ex.region && (
        <span className="absolute top-2 left-2 text-[9px] uppercase tracking-wide text-[#737373] font-semibold">{ex.region}</span>
      )}
      <span className={cn("absolute top-2 right-2 w-4 h-4 rounded-full border-2 flex items-center justify-center",
        selected ? "border-[#D4AF37]" : "border-[#525252]")}>
        {selected && <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />}
      </span>
      <span className="w-11 h-11 rounded-full bg-[#141414] border border-[#262626] flex items-center justify-center font-heading font-bold text-sm" style={{ color: ex.color }}>
        {ex.name.slice(0, 2).toUpperCase()}
      </span>
      <span className="text-sm font-medium" style={{ color: ex.color }}>{ex.name}</span>
    </button>
  );
}

export function AddClientPanel({ open, onClose }) {
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(null);
  const [accountName, setAccountName] = useState("");
  const [exType, setExType] = useState("both");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: trusted } = useQuery({ queryKey: ["trusted-ips"], queryFn: async () => (await api.get("/config/trusted-ips")).data, enabled: open });

  const reset = () => {
    setStep(1); setSelected(null); setAccountName(""); setExType("both");
    setApiKey(""); setApiSecret(""); setShowKey(false); setShowSecret(false);
  };
  const close = () => { reset(); onClose(); };

  const submit = async () => {
    if (!apiKey.trim() || !apiSecret.trim()) { toast.error("API key and secret are required."); return; }
    setSaving(true);
    try {
      const test = await api.post("/partner/clients/test-connection", {
        exchange_slug: selected.slug.split("-")[0], market: exType, api_key: apiKey, api_secret: apiSecret,
      });
      if (!test.data.ok && test.data.code === "auth") { toast.error(test.data.message); setSaving(false); return; }
      await api.post("/partner/clients", {
        account_name: accountName.trim() || `${selected.name} Client`,
        exchange_slug: selected.slug, market: exType, api_key: apiKey, api_secret: apiSecret,
        region: selected.region || null,
      });
      qc.invalidateQueries({ queryKey: ["partner-clients"] });
      toast.success("Client account connected");
      close();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" onClick={close} data-testid="add-client-overlay" />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.28, ease: "easeOut" }}
            className="fixed top-0 right-0 z-50 h-full w-full max-w-2xl bg-[#0A0A0A] border-l border-[#262626] flex flex-col"
            data-testid="add-client-panel"
          >
            <button onClick={close} data-testid="add-client-close"
              className="absolute top-4 right-4 z-10 w-8 h-8 rounded-md border border-[#D4AF37] text-[#D4AF37] flex items-center justify-center hover:bg-[rgba(212,175,55,0.1)] transition-colors">
              <X className="w-4 h-4" />
            </button>

            <div className="flex-1 overflow-y-auto px-7 py-7">
              {step === 1 ? (
                <>
                  <h2 className="font-heading text-xl font-bold text-[#F5F5F5] pr-10">Choose an Exchange to Connect a New API Key</h2>
                  <p className="text-sm text-[#A3A3A3] mt-2 leading-relaxed">
                    We support integration with several exchanges. Use the guide below to gain a better understanding of the process to connect your API key. It is recommended to restrict access to trusted IPs only to increase your account security.
                  </p>
                  <div className="text-sm font-medium text-[#F5F5F5] mt-6 mb-3">Select an exchange</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {CONNECT_EXCHANGES.map((ex) => (
                      <ExchangeTile key={ex.slug} ex={ex} selected={selected?.slug === ex.slug} onSelect={setSelected} />
                    ))}
                  </div>

                  <div className="mt-7 rounded-md border border-[#262626] bg-[#0D0D0D] p-4">
                    <div className="text-sm font-medium text-[#F5F5F5] mb-1">Frequently Asked Questions (FAQ)</div>
                    <Accordion type="single" collapsible className="w-full">
                      {PICKER_FAQ.map((f, i) => (
                        <AccordionItem key={i} value={`f${i}`} className="border-[#1c1c1c]">
                          <AccordionTrigger className="text-sm text-[#F5F5F5] hover:no-underline">{f.q}</AccordionTrigger>
                          <AccordionContent className="text-[13px] text-[#A3A3A3]">{f.a}</AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="font-heading text-xl font-bold text-[#F5F5F5] pr-10 flex items-center gap-2">
                    Connect your <span style={{ color: selected.color }}>{selected.name}</span> API key
                  </h2>
                  <p className="text-sm text-[#A3A3A3] mt-2 leading-relaxed">
                    To get started, generate a new API key on your {selected.name} account. Then copy and paste your API key and Secret into the boxes below. Refer to the guide for detailed instructions on how to create a key and whitelist the IPs.
                  </p>

                  <div className="grid lg:grid-cols-2 gap-6 mt-6">
                    <div className="rounded-md border border-[#262626] bg-[#0D0D0D] p-4 h-fit">
                      <div className="text-sm font-medium text-[#F5F5F5] mb-1">Frequently Asked Questions (FAQ)</div>
                      <Accordion type="single" collapsible className="w-full">
                        {CONNECT_FAQ.map((f, i) => (
                          <AccordionItem key={i} value={`c${i}`} className="border-[#1c1c1c]">
                            <AccordionTrigger className="text-sm text-[#F5F5F5] text-left hover:no-underline">{f.q}</AccordionTrigger>
                            <AccordionContent className="text-[13px] text-[#A3A3A3]">{f.a}</AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="text-xs text-[#A3A3A3] mb-1.5 block">Account Name</label>
                        <input value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="example: TraderJohn" data-testid="client-account-name"
                          className="w-full bg-[#0A0A0A] border border-[#333333] rounded-sm px-3 py-2 text-sm text-[#F5F5F5] placeholder:text-[#525252] focus:outline-none focus:border-[#D4AF37]" />
                      </div>

                      <div>
                        <label className="text-xs text-[#A3A3A3] mb-2 block">Exchange Type</label>
                        <div className="flex items-center gap-5">
                          {EXCHANGE_TYPES.map((t) => (
                            <button key={t.value} onClick={() => setExType(t.value)} data-testid={`client-extype-${t.value}`} className="flex items-center gap-2 text-sm text-[#F5F5F5]">
                              <span className={cn("w-4 h-4 rounded-full border-2 flex items-center justify-center", exType === t.value ? "border-[#D4AF37]" : "border-[#525252]")}>
                                {exType === t.value && <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />}
                              </span>
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="rounded-md border border-[#262626] bg-[#0D0D0D] p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-[#A3A3A3]">You must white-list these IPs on {selected.name}</span>
                          <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" />
                        </div>
                        <div className="flex items-center justify-between bg-[#141414] border border-[#262626] rounded-sm px-3 py-2">
                          <span className="font-mono text-sm text-[#F5F5F5]">{(trusted?.ips || []).join("  ")}</span>
                          <button onClick={() => { navigator.clipboard.writeText((trusted?.ips || []).join(" ")); toast.success("Copied"); }} data-testid="client-copy-ips" className="text-[#737373] hover:text-[#D4AF37]">
                            <CopyIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs text-[#A3A3A3] mb-1.5 block">API key provided by exchange</label>
                        <div className="relative">
                          <input type={showKey ? "text" : "password"} value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="••••" data-testid="client-api-key"
                            className="w-full bg-[#0A0A0A] border border-[#333333] rounded-sm px-3 py-2 pr-9 text-sm text-[#F5F5F5] font-mono focus:outline-none focus:border-[#D4AF37]" />
                          <button onClick={() => setShowKey((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#F5F5F5]">
                            {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-[#A3A3A3] mb-1.5 block">Secret key provided by exchange</label>
                        <div className="relative">
                          <input type={showSecret ? "text" : "password"} value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} placeholder="••••" data-testid="client-api-secret"
                            className="w-full bg-[#0A0A0A] border border-[#333333] rounded-sm px-3 py-2 pr-9 text-sm text-[#F5F5F5] font-mono focus:outline-none focus:border-[#D4AF37]" />
                          <button onClick={() => setShowSecret((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#F5F5F5]">
                            {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer actions */}
            <div className="border-t border-[#262626] px-7 py-4 flex items-center gap-3">
              {step === 2 && (
                <button onClick={() => setStep(1)} data-testid="add-client-back" className="flex-1 flex items-center justify-center gap-2 border border-[#333333] text-[#F5F5F5] text-sm rounded-sm py-2.5 hover:border-[#525252] transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              )}
              <button
                onClick={() => (step === 1 ? (selected ? setStep(2) : toast.error("Select an exchange first")) : submit())}
                disabled={saving || (step === 1 && !selected)}
                data-testid="add-client-next"
                className="flex-1 flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#F0C74B] disabled:opacity-50 text-[#0A0A0A] font-semibold text-sm rounded-sm py-2.5 transition-colors"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Next <ChevronRight className="w-4 h-4" /></>}
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
